import { Suspense, lazy, useEffect, useState, type ReactNode } from "react";
import { Box, ButtonBase, Stack, Tooltip, Typography, alpha } from "@mui/material";
import { AnimatePresence, motion, useScroll, useSpring, useTransform, useVelocity } from "framer-motion";
import CircularText from "./CircularText";
import PlasmaBackground from "./PlasmaBackground";
import type { ModuleChromeSlot } from "./ModuleChromeContext";
import { useAuth } from "../contexts/AuthContext";
import { useThemeMode } from "../contexts/ThemeContext";
import {
  appChrome,
  appChromeBorderColor,
  appChromeShadow,
  appChromeSurfaceFill,
  brandPlasmaAmbientLayerSx,
  brandPlasmaCanvasFilter,
  brandPlasmaChromeProps,
  brandPlasmaVeilLayerSx,
  palette,
  typographyTokens,
  uiLayers,
} from "../theme/tokens";
import { CleanPanelRow, cleanDividerSx } from "../ui/Clean";
import { Icons } from "../ui/icons";
import { UserAvatar, type UserAvatarUser } from "../ui/UserAvatar";

export type CardNavItem = {
  id: string;
  label: string;
  module: string;
};

export type CardNavGroup = {
  id: string;
  title: string;
  items: CardNavItem[];
};

type AppCardNavProps = {
  open: boolean;
  groups: CardNavGroup[];
  activeModule?: string;
  onToggle: () => void;
  onClose: () => void;
  onNavigate: (module: string) => void;
  onBrandClick: () => void;
  moduleChrome?: ModuleChromeSlot | null;
  profileMenuOpen?: boolean;
  onProfileMenuOpenChange?: (open: boolean) => void;
  notificationMenuOpen?: boolean;
  onNotificationMenuOpenChange?: (open: boolean) => void;
};

type ChromePanelMode = "menu" | "notifications" | "profile" | null;
const NotificationBellButton = lazy(() => import("./NotificationBell").then((module) => ({ default: module.NotificationBellButton })));
const NotificationCenterPanel = lazy(() => import("./NotificationBell").then((module) => ({ default: module.NotificationCenterPanel })));

const TOP_HEIGHT = 64;
const TOP_MASK_HEIGHT = TOP_HEIGHT;
const HEADER_SPACE = { xs: 80, md: 88 };
const HEADER_Z_INDEX = uiLayers.chrome;
const PROFILE_PANEL_WIDTH = 278;
const NOTIFICATION_PANEL_WIDTH = 480;

const GROUP_ACCENTS = [
  palette.accent.cyan,
  palette.accent.mint,
  palette.accent.amber,
  palette.accent.blue,
  palette.accent.red,
  palette.accent.silver,
  palette.accent.violetSoft,
  palette.accent.graphite,
];

const panelSpring = { type: "spring" as const, stiffness: 360, damping: 32 };
const itemSpring = { type: "spring" as const, stiffness: 480, damping: 34 };
const panelRevealTransition = {
  height: { duration: 0.22, ease: [0.22, 1, 0.36, 1] },
  opacity: { duration: 0.14, ease: "easeOut" },
};
const chromePlasmaQuality = { maxDpr: 0.65, targetFps: 20, iterations: 38 } as const;

const chromeTextColor = (t: any, opacity = 0.9) =>
  t.palette.mode === "dark" ? alpha(palette.text.darkPrimary, opacity) : alpha(t.palette.text.primary, opacity);

const chromeMutedTextColor = (t: any, opacity = 0.66) =>
  t.palette.mode === "dark" ? alpha(palette.text.darkSecondary, opacity) : alpha(t.palette.text.primary, opacity);

function formatSectionCount(count: number) {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return `${count} раздел`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} раздела`;
  return `${count} разделов`;
}

const rightPanelChromeMaskSx = (width: number) => ({
  WebkitMaskImage: "linear-gradient(#000 0 0), linear-gradient(#000 0 0)",
  maskImage: "linear-gradient(#000 0 0), linear-gradient(#000 0 0)",
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
  WebkitMaskSize: {
    xs: `100% ${TOP_MASK_HEIGHT}px, min(${width}px, calc(100% - 12px)) calc(100% - ${TOP_HEIGHT}px)`,
    sm: `100% ${TOP_MASK_HEIGHT}px, ${width}px calc(100% - ${TOP_HEIGHT}px)`,
  },
  maskSize: {
    xs: `100% ${TOP_MASK_HEIGHT}px, min(${width}px, calc(100% - 12px)) calc(100% - ${TOP_HEIGHT}px)`,
    sm: `100% ${TOP_MASK_HEIGHT}px, ${width}px calc(100% - ${TOP_HEIGHT}px)`,
  },
  WebkitMaskPosition: {
    xs: `left top, right 0px top ${TOP_HEIGHT}px`,
    sm: `left top, right 0px top ${TOP_HEIGHT}px`,
    md: `left top, right 0px top ${TOP_HEIGHT}px`,
  },
  maskPosition: {
    xs: `left top, right 0px top ${TOP_HEIGHT}px`,
    sm: `left top, right 0px top ${TOP_HEIGHT}px`,
    md: `left top, right 0px top ${TOP_HEIGHT}px`,
  },
}) as const;

const profileChromeMaskSx = rightPanelChromeMaskSx(PROFILE_PANEL_WIDTH);
const notificationChromeMaskSx = rightPanelChromeMaskSx(NOTIFICATION_PANEL_WIDTH);

const headerControlSx = (active: boolean) => ({
  width: 42,
  height: 42,
  borderRadius: 1,
  display: "grid",
  placeItems: "center",
  color: (t) => chromeTextColor(t, active ? 0.98 : 0.88),
  border: "1px solid",
  borderColor: (t) =>
    t.palette.mode === "dark"
      ? alpha(palette.accent.silver, active ? 0.16 : 0.075)
      : alpha(t.palette.text.primary, active ? 0.12 : 0.045),
  bgcolor: (t) =>
    t.palette.mode === "dark"
      ? alpha(palette.dark.surfaceElevated, active ? 0.22 : 0.045)
      : alpha(t.palette.background.paper, active ? 0.24 : 0.03),
  boxShadow: active ? "inset 0 1px 0 rgba(255,255,255,0.045)" : "none",
  transition: "background-color .16s ease, border-color .16s ease, color .16s ease",
  "& svg": {
    opacity: 1,
  },
  "&:hover": {
    color: (t) => chromeTextColor(t, 0.98),
    borderColor: (t) =>
      t.palette.mode === "dark"
        ? alpha(palette.accent.silver, 0.13)
        : alpha(t.palette.text.primary, 0.08),
    bgcolor: (t) =>
      t.palette.mode === "dark"
        ? alpha(palette.dark.surfaceElevated, 0.12)
        : alpha(t.palette.background.paper, 0.12),
  },
  "&:active": {
    bgcolor: (t) =>
      t.palette.mode === "dark"
        ? alpha(palette.dark.surfaceElevated, 0.18)
        : alpha(t.palette.background.paper, 0.18),
  },
});

const moduleChromeControlSx = headerControlSx(false);

const chromeSurfaceLayerSx = {
  position: "absolute",
  inset: 0,
  zIndex: 1,
  pointerEvents: "none",
  bgcolor: (t) =>
    appChromeSurfaceFill(
      t,
      t.palette.mode === "dark" ? 0.26 : 0.34,
    ),
  backdropFilter: appChrome.backdropFilter,
  WebkitBackdropFilter: appChrome.backdropFilter,
} as const;

const chromePlasmaCanvasSx = {
  position: "absolute",
  zIndex: 0,
  pointerEvents: "none",
  top: { xs: -116, md: -150 },
  right: { xs: -88, md: -116 },
  left: { xs: -88, md: -116 },
  height: { xs: 760, md: 840 },
  opacity: (t) => (t.palette.mode === "dark" ? 0.92 : 0.62),
  filter: brandPlasmaCanvasFilter,
} as const;

function ChromePlasmaLayer({
  visualPanel,
}: {
  visualPanel: ChromePanelMode;
}) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
        overflow: "hidden",
        borderRadius: 1,
        bgcolor: (t) =>
          appChromeSurfaceFill(
            t,
            t.palette.mode === "dark" ? 0.44 : 0.5,
          ),
        backdropFilter: appChrome.backdropFilter,
        WebkitBackdropFilter: appChrome.backdropFilter,
        boxShadow: (t) => appChromeShadow(t),
        transition:
          "background-color .18s ease, -webkit-mask-size .22s ease, -webkit-mask-position .22s ease, mask-size .22s ease, mask-position .22s ease",
        ...(visualPanel === "profile"
          ? profileChromeMaskSx
          : visualPanel === "notifications"
            ? notificationChromeMaskSx
            : {}),
      }}
    >
      <Box sx={brandPlasmaAmbientLayerSx} />
      <Box sx={chromePlasmaCanvasSx}>
        <PlasmaBackground {...brandPlasmaChromeProps} {...chromePlasmaQuality} />
      </Box>
      <Box aria-hidden="true" sx={brandPlasmaVeilLayerSx} />
    </Box>
  );
}

function ProfileMenuPanel({
  onNavigate,
  onClose,
}: {
  onNavigate: (module: string) => void;
  onClose: () => void;
}) {
  const { user, hasAccess, logout } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  const displayName = user?.name || user?.email || "Пользователь";
  const displayRole =
    user?.businessRole || (user?.role === "admin" ? "Администратор" : user?.role === "lead" ? "Старший" : "Пользователь");

  const modeIcon =
    mode === "dark" ? (
      <Icons.Moon size={17} />
    ) : mode === "light" ? (
      <Icons.Sun size={17} />
    ) : (
      <Icons.MonitorCog size={17} />
    );

  const modeLabel =
    mode === "dark"
      ? "Тёмная тема"
      : mode === "light"
        ? "Светлая тема"
        : "Системная тема";

  const navigateAndClose = (module: string) => {
    onNavigate(module);
    onClose();
  };

  return (
    <Box
      id="app-card-nav-profile-menu"
      sx={{
        position: "relative",
        zIndex: 1,
        border: "1px solid",
        borderTopWidth: 0,
        borderColor: (t) => appChromeBorderColor(t),
        borderRadius: "0 0 8px 8px",
        px: { xs: 0.75, sm: 1, md: 1.25 },
        py: { xs: 0.75, sm: 1 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          boxSizing: "border-box",
          maxHeight: "calc(100dvh - 96px)",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <StackProfileIdentity user={user} title={displayName} subtitle={displayRole} />
        <ProfileMenuAction icon={<Icons.User size={17} />} label="Профиль" onClick={() => navigateAndClose("profile")} />
        {hasAccess("admin") && (
          <ProfileMenuAction
            icon={<Icons.Admin size={17} />}
            label="Пользователи"
            onClick={() => navigateAndClose("admin")}
          />
        )}
        <ProfileMenuAction icon={modeIcon} label={modeLabel} onClick={toggleMode} />
        <ProfileMenuDivider />
        <ProfileMenuAction
          icon={<Icons.LogOut size={17} />}
          label="Выйти"
          onClick={() => {
            onClose();
            logout();
          }}
        />
      </Box>
    </Box>
  );
}

function ProfileMenuDivider() {
  return <Box aria-hidden="true" sx={cleanDividerSx} />;
}

function StackProfileIdentity({
  user,
  title,
  subtitle,
}: {
  user?: UserAvatarUser | null;
  title: string;
  subtitle: string;
}) {
  return (
    <Stack
      direction="row"
      spacing={0.9}
      alignItems="center"
      sx={{
        px: 1.35,
        py: 1.05,
        minWidth: 0,
      }}
    >
      <UserAvatar user={user} size={34} />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            minWidth: 0,
            fontSize: 13.2,
            fontWeight: typographyTokens.weights.bold,
            lineHeight: 1.18,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ mt: 0.35, fontSize: 11.6, lineHeight: 1.25, color: "text.secondary" }}>
          {subtitle}
        </Typography>
      </Box>
    </Stack>
  );
}

function ProfileMenuAction({
  icon,
  label,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <CleanPanelRow
      icon={icon}
      label={label}
      onClick={onClick}
    />
  );
}

function ModuleChromeButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick?: () => void;
  children: ReactNode;
}) {
  if (!onClick) return null;

  return (
    <Tooltip title={label}>
      <ButtonBase onClick={onClick} aria-label={label} sx={moduleChromeControlSx}>
        {children}
      </ButtonBase>
    </Tooltip>
  );
}

function ModuleIslandPlasmaLayer({ expanded }: { expanded: boolean }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
        overflow: "hidden",
        borderRadius: 1,
        bgcolor: (t) =>
          appChromeSurfaceFill(
            t,
            t.palette.mode === "dark"
              ? expanded ? 0.48 : 0.38
              : expanded ? 0.54 : 0.44,
          ),
        backdropFilter: appChrome.backdropFilter,
        WebkitBackdropFilter: appChrome.backdropFilter,
        boxShadow: (t) => appChromeShadow(t),
      }}
    >
      <Box sx={brandPlasmaAmbientLayerSx} />
      <Box aria-hidden="true" sx={brandPlasmaVeilLayerSx} />
    </Box>
  );
}

function ModuleTitleIsland({ expanded, slot }: { expanded: boolean; slot?: ModuleChromeSlot | null }) {
  if (!slot?.title) return null;
  const hasControls = Boolean(slot.onHelpClick || slot.onSettingsClick);

  return (
    <Box
      sx={{
        display: { xs: "none", md: "grid" },
        position: "relative",
        zIndex: 2,
        width: "100%",
        minWidth: 0,
        height: TOP_HEIGHT,
        pointerEvents: "auto",
        boxSizing: "border-box",
        border: "1px solid",
        borderColor: (t) => appChromeBorderColor(t),
        borderRadius: 1,
        overflow: "hidden",
        alignItems: "center",
        gridTemplateColumns: hasControls ? "minmax(0, 1fr) auto" : "minmax(0, 1fr)",
        gap: 0.75,
        px: 1.25,
      }}
    >
      <ModuleIslandPlasmaLayer expanded={expanded} />
      <Box aria-hidden="true" sx={chromeSurfaceLayerSx} />
      <Box sx={{ minWidth: 0 }}>
        {slot.eyebrow && (
          <Typography
            component="div"
            sx={{
              position: "relative",
              zIndex: 2,
              mb: 0.25,
              fontSize: 10.1,
              fontWeight: typographyTokens.weights.semibold,
              lineHeight: 1,
              color: (t) =>
                chromeMutedTextColor(t, 0.82),
              letterSpacing: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {slot.eyebrow}
          </Typography>
        )}
        <Typography
          component="div"
          sx={{
            position: "relative",
            zIndex: 2,
            minWidth: 0,
            fontSize: 14.6,
            fontWeight: typographyTokens.weights.bold,
            lineHeight: 1.12,
            color: (t) => chromeTextColor(t, 0.99),
            letterSpacing: 0,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            whiteSpace: "normal",
            overflowWrap: "normal",
          }}
        >
          {slot.title}
        </Typography>
      </Box>
      {hasControls && (
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            ml: 0.25,
          }}
        >
          <ModuleChromeButton label={slot.helpLabel ?? "Справка"} onClick={slot.onHelpClick}>
            <Icons.Help size={19} />
          </ModuleChromeButton>
          <ModuleChromeButton label={slot.settingsLabel ?? "Настройки"} onClick={slot.onSettingsClick}>
            <Icons.Settings size={19} />
          </ModuleChromeButton>
        </Box>
      )}
    </Box>
  );
}

export default function AppCardNav({
  open,
  groups,
  activeModule,
  onToggle,
  onClose,
  onNavigate,
  onBrandClick,
  moduleChrome,
  profileMenuOpen = false,
  onProfileMenuOpenChange,
  notificationMenuOpen = false,
  onNotificationMenuOpenChange,
}: AppCardNavProps) {
  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);
  const inertialY = useSpring(useTransform(scrollVelocity, [-2400, 0, 2400], [-2.5, 0, 2.5]), {
    stiffness: 220,
    damping: 30,
    mass: 0.25,
  });

  const chromeOverlayOpen = open || notificationMenuOpen || profileMenuOpen;
  const activePanel: ChromePanelMode = open ? "menu" : notificationMenuOpen ? "notifications" : profileMenuOpen ? "profile" : null;
  const [exitingPanel, setExitingPanel] = useState<ChromePanelMode>(null);
  const visualPanel = activePanel ?? exitingPanel;
  const chromeVisuallyExpanded = visualPanel !== null;
  const closeProfileMenu = () => onProfileMenuOpenChange?.(false);
  const closeNotificationMenu = () => onNotificationMenuOpenChange?.(false);
  const handleNotificationOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      onClose();
      closeProfileMenu();
    }
    onNotificationMenuOpenChange?.(nextOpen);
  };
  const handleProfileToggle = () => {
    if (profileMenuOpen) {
      closeProfileMenu();
      return;
    }
    onClose();
    closeNotificationMenu();
    onProfileMenuOpenChange?.(true);
  };

  useEffect(() => {
    if (activePanel) {
      setExitingPanel(activePanel);
    }
  }, [activePanel]);

  return (
    <>
      <AnimatePresence initial={false}>
        {chromeOverlayOpen && (
          <Box
            component={motion.div}
            aria-hidden="true"
            onClick={open ? onClose : notificationMenuOpen ? closeNotificationMenu : closeProfileMenu}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 1200,
              cursor: "default",
              pointerEvents: "auto",
              bgcolor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(3,7,13,0.46)"
                  : "rgba(248,250,252,0.46)",
              backdropFilter: "blur(4px) contrast(0.78) saturate(0.82)",
              WebkitBackdropFilter: "blur(4px) contrast(0.78) saturate(0.82)",
            }}
          />
        )}
      </AnimatePresence>

      <Box
        component="header"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: HEADER_Z_INDEX,
          px: { xs: 1, sm: 1.5, md: 2 },
          py: { xs: 0.75, md: 1 },
          bgcolor: "transparent",
          backdropFilter: "none",
          WebkitBackdropFilter: "none",
          pointerEvents: "none",
        }}
      >
        <Box
          component={motion.div}
          style={{ y: inertialY }}
          initial={false}
          sx={{
            width: "100%",
            maxWidth: 1180,
            mx: "auto",
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: moduleChrome
                ? "minmax(0, 1fr) minmax(240px, 0.35fr)"
                : "minmax(0, 1fr)",
            },
            justifyContent: "start",
            alignItems: "start",
            gap: { xs: 0.75, md: 1 },
            transformOrigin: "50% 0",
            willChange: "transform",
          }}
        >
      <Box
        component="nav"
        aria-label="Основная навигация"
        sx={{
          minWidth: 0,
          minHeight: TOP_HEIGHT,
          position: "relative",
          overflow: "hidden",
          pointerEvents: "none",
          borderRadius: 1,
          contain: "layout paint",
        }}
      >
        <ChromePlasmaLayer visualPanel={visualPanel} />
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            height: TOP_HEIGHT,
            pointerEvents: "auto",
            boxSizing: "border-box",
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
            gap: 1,
            px: { xs: 0.75, sm: 1, md: 1.25 },
            border: "1px solid",
            borderColor: (t) => appChromeBorderColor(t),
            borderRadius: 1,
            overflow: "hidden",
            bgcolor: (t) =>
              appChromeSurfaceFill(
                t,
                t.palette.mode === "dark"
                  ? chromeVisuallyExpanded ? 0.34 : 0.26
                  : chromeVisuallyExpanded ? 0.42 : 0.34,
              ),
            backdropFilter: appChrome.backdropFilter,
            WebkitBackdropFilter: appChrome.backdropFilter,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: { xs: 0.55, sm: 0.75 },
              minWidth: 0,
            }}
          >
            <ButtonBase
              onClick={onToggle}
              aria-label={open ? "Закрыть меню" : "Открыть меню"}
              aria-expanded={open}
              aria-controls="app-card-nav-panel"
              sx={headerControlSx(open)}
            >
              <Box
                component={motion.span}
                initial={false}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.14, ease: "easeOut" }}
                sx={{ position: "relative", zIndex: 1, display: "grid", placeItems: "center" }}
              >
                {open ? <Icons.X size={19} /> : <Icons.Grid size={20} />}
              </Box>
            </ButtonBase>
          </Box>

          <ButtonBase
            onClick={onBrandClick}
            aria-label="Warehouse Hub, перейти на дашборд"
            sx={{
              width: 52,
              height: 52,
              minWidth: 52,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              color: (t) => chromeTextColor(t, 0.96),
              transform: "translateZ(0)",
              transition: "opacity .16s ease, background-color .16s ease",
              opacity: 1,
              "&:hover": {
                opacity: 1,
                bgcolor: (t) =>
                  t.palette.mode === "dark"
                    ? "rgba(209,215,224,0.035)"
                    : "rgba(15,23,42,0.035)",
              },
            }}
          >
            <CircularText text="WH•OPTI•HUB•" diameter={46} fontSize={9.5} spinDuration={22} />
          </ButtonBase>

          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: { xs: 0.45, sm: 0.55 } }}>
            <Suspense
              fallback={
                <Box aria-hidden="true" sx={{ ...headerControlSx(false), pointerEvents: "none" }}>
                  <Icons.Bell size={18} />
                </Box>
              }
            >
              <NotificationBellButton
                open={notificationMenuOpen}
                buttonSx={headerControlSx(notificationMenuOpen)}
                iconSize={18}
                onOpenChange={(nextOpen) => handleNotificationOpenChange(nextOpen)}
              />
            </Suspense>
            <ButtonBase
              onClick={handleProfileToggle}
              aria-label={profileMenuOpen ? "Закрыть меню профиля" : "Открыть меню профиля"}
              aria-haspopup="menu"
              aria-expanded={profileMenuOpen}
              aria-controls="app-card-nav-profile-menu"
              sx={headerControlSx(profileMenuOpen)}
            >
              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  width: 28,
                  height: 28,
                  display: "grid",
                  placeItems: "center",
                  color: "inherit",
                }}
              >
                <Icons.User size={17} />
              </Box>
            </ButtonBase>
          </Box>
        </Box>

        <AnimatePresence initial={false} onExitComplete={() => setExitingPanel(null)}>
          {open ? (
            <Box
              key="nav-panel"
              id="app-card-nav-panel"
              component={motion.div}
              initial={{ height: 0, opacity: 1 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 1 }}
              transition={panelRevealTransition}
              sx={{
                position: "relative",
                zIndex: 1,
                pointerEvents: "auto",
                boxSizing: "border-box",
                borderLeft: "1px solid",
                borderRight: "1px solid",
                borderBottom: "1px solid",
                borderColor: (t) =>
                  appChromeBorderColor(t),
                borderRadius: "0 0 8px 8px",
                overflow: "hidden",
                transformOrigin: "50% 0",
              }}
            >
              <Box
                sx={{
                  px: { xs: 0.75, sm: 1, md: 1.25 },
                  pt: { xs: 0.75, sm: 1, md: 1.25 },
                  pb: { xs: 0.75, sm: 1, md: 1.25 },
                }}
              >
                <Box
                  sx={{
                    maxHeight: { xs: "calc(100dvh - 108px)", md: "min(calc(100dvh - 120px), 610px)" },
                    overflowY: "auto",
                    overflowX: "hidden",
                    scrollbarGutter: "stable",
                    pr: { xs: 0, sm: 0.25 },
                    scrollbarWidth: "thin",
                    "&::-webkit-scrollbar": { width: 8 },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: (t) =>
                        t.palette.mode === "dark"
                          ? "rgba(209,215,224,.24)"
                          : "rgba(15,23,42,.20)",
                      borderRadius: 8,
                      border: "2px solid transparent",
                      backgroundClip: "content-box",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        lg: "repeat(3, minmax(0, 1fr))",
                      },
                      gap: 1,
                    }}
                  >
                    {groups.map((group, index) => (
                      <NavGroupCard
                        key={group.id}
                        group={group}
                        index={index}
                        activeModule={activeModule}
                        onNavigate={onNavigate}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : notificationMenuOpen ? (
            <Box
              key="notifications-panel"
              component={motion.div}
              initial={{ height: 0, opacity: 1 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 1 }}
              transition={panelRevealTransition}
              sx={{
                position: "relative",
                zIndex: 1,
                width: {
                  xs: `min(${NOTIFICATION_PANEL_WIDTH}px, calc(100% - 12px))`,
                  sm: NOTIFICATION_PANEL_WIDTH,
                },
                ml: "auto",
                mr: 0,
                pointerEvents: "auto",
                overflow: "hidden",
                transformOrigin: "50% 0",
              }}
            >
              <Suspense
                fallback={
                  <Box
                    sx={{
                      border: "1px solid",
                      borderTopWidth: 0,
                      borderColor: (theme) => appChromeBorderColor(theme),
                      borderRadius: "0 0 8px 8px",
                      p: 1.35,
                    }}
                  >
                    <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                      Загружаю уведомления...
                    </Typography>
                  </Box>
                }
              >
                <NotificationCenterPanel attached open={notificationMenuOpen} onRequestClose={closeNotificationMenu} />
              </Suspense>
            </Box>
          ) : profileMenuOpen ? (
            <Box
              key="profile-panel"
              component={motion.div}
              initial={{ height: 0, opacity: 1 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 1 }}
              transition={panelRevealTransition}
              sx={{
                position: "relative",
                zIndex: 1,
                width: {
                  xs: `min(${PROFILE_PANEL_WIDTH}px, calc(100% - 12px))`,
                  sm: PROFILE_PANEL_WIDTH,
                },
                ml: "auto",
                mr: 0,
                pointerEvents: "auto",
                overflow: "hidden",
                transformOrigin: "50% 0",
              }}
            >
              <ProfileMenuPanel onNavigate={onNavigate} onClose={closeProfileMenu} />
            </Box>
          ) : null}
        </AnimatePresence>
      </Box>
          <ModuleTitleIsland expanded={chromeVisuallyExpanded} slot={moduleChrome} />
        </Box>
      </Box>

      <Box
        aria-hidden="true"
        sx={{
          height: HEADER_SPACE,
          flexShrink: 0,
        }}
      />
    </>
  );
}

function NavGroupCard({
  group,
  index,
  activeModule,
  onNavigate,
}: {
  group: CardNavGroup;
  index: number;
  activeModule?: string;
  onNavigate: (module: string) => void;
}) {
  const accent = GROUP_ACCENTS[index % GROUP_ACCENTS.length];

  return (
    <Box
      component={motion.section}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...itemSpring, delay: index * 0.035 }}
      sx={{
        minHeight: { xs: 122, sm: 150 },
        borderRadius: 1,
        p: { xs: 1.2, sm: 1.35 },
        display: "flex",
        flexDirection: "column",
        gap: 1,
        color: "text.primary",
        border: "1px solid",
        borderColor: (t) =>
          t.palette.mode === "dark" ? alpha(accent, 0.24) : alpha(accent, 0.28),
        bgcolor: (t) =>
          t.palette.mode === "dark"
            ? alpha(accent, 0.075)
            : alpha(accent, index % 2 === 0 ? 0.08 : 0.06),
        boxShadow: (t) =>
          t.palette.mode === "dark"
            ? "inset 0 1px 0 rgba(255,255,255,0.025)"
            : "inset 0 1px 0 rgba(255,255,255,0.65)",
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: typographyTokens.weights.bold,
            lineHeight: 1.12,
            letterSpacing: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {group.title}
        </Typography>
        <Typography
          sx={{
            mt: 0.35,
            fontSize: 11,
            lineHeight: 1,
            color: "text.secondary",
            letterSpacing: 0,
          }}
        >
          {formatSectionCount(group.items.length)}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.25,
        }}
      >
        {group.items.map((item) => {
          const selected = activeModule === item.module;
          return (
            <ButtonBase
              key={item.id}
              onClick={() => onNavigate(item.module)}
              aria-current={selected ? "page" : undefined}
              sx={{
                width: "100%",
                minHeight: 34,
                px: 0.8,
                py: 0.55,
                borderRadius: 0.75,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                color: selected ? "text.primary" : "text.secondary",
                textAlign: "left",
                bgcolor: (t) =>
                  selected
                    ? t.palette.mode === "dark"
                      ? alpha(accent, 0.18)
                      : alpha("#ffffff", 0.74)
                    : "transparent",
                boxShadow: selected ? `inset 2px 0 0 ${alpha(accent, 0.58)}, inset 0 0 0 1px ${alpha(accent, 0.22)}` : "none",
                transition: "background-color .16s ease, box-shadow .16s ease, color .16s ease",
                "&:hover": {
                  color: "text.primary",
                  bgcolor: (t) =>
                    t.palette.mode === "dark"
                      ? alpha(accent, selected ? 0.2 : 0.12)
                      : alpha("#ffffff", selected ? 0.82 : 0.62),
                },
              }}
            >
              <Typography
                component="span"
                sx={{
                  minWidth: 0,
                  fontSize: 12.5,
                  fontWeight: selected ? typographyTokens.weights.semibold : typographyTokens.weights.medium,
                  lineHeight: 1.2,
                  letterSpacing: 0,
                  overflowWrap: "anywhere",
                }}
              >
                {item.label}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}
